import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import RangeSlider from 'react-range-slider-input';
import 'react-range-slider-input/dist/style.css';
import { CustomChart } from '../../Components/Chart/CustomChart';
import { PriceInput } from './FilterPriceInput';
import { useDispatch, useSelector } from 'react-redux';
import { 
  setPriceRange, 
  setPriceRangeStats, 
  setListingsInRange, 
  setIsLoadingPriceStats 
} from '../../ReduxSlices/SearchSlice';
import { getPriceRangeStats, getListingsInPriceRange, formatPrice } from '../functions/PriceRangeApi';
import { normalizePricePerDay } from '../../utils/propertyValidation';

export const PriceRangeSection = ({ range, onRangeChange, properties = [] }) => {
  const dispatch = useDispatch();
  
  // Redux state
  const priceRangeStats = useSelector((state) => state.search.priceRangeStats);
  const listingsInRange = useSelector((state) => state.search.listingsInRange);
  const isLoadingPriceStats = useSelector((state) => state.search.isLoadingPriceStats);
  
  // Debug: Log properties received
  useEffect(() => {
    console.log('PriceRangeSection - Properties received:', {
      count: properties?.length || 0,
      sample: properties?.slice(0, 3).map(p => ({
        token_id: p?.token_id,
        price: p?.short?.price_per_day
      }))
    });
  }, [properties]);

  // Refs to prevent infinite re-renders
  const isInitialized = useRef(false);
  const lastRangeRef = useRef(range);
  
  // Calculate actual max price from properties (ignoring the highest outlier)
  const calculateMaxPriceFromProperties = useCallback(() => {
    if (!properties || properties.length === 0) {
      console.log('No properties available for max price calculation');
      return null;
    }
    
    // Collect all normalized prices
    const prices = [];
    properties.forEach(property => {
      // Normalize price to handle both raw format and converted format
      const rawPrice = property?.short?.price_per_day || 0;
      const price = normalizePricePerDay(rawPrice);
      if (price > 0) {
        prices.push({
          price: price,
          token_id: property?.token_id
        });
      }
    });
    
    if (prices.length === 0) {
      console.log('No valid prices found in properties');
      return null;
    }
    
    // Sort prices in descending order
    prices.sort((a, b) => b.price - a.price);
    
    // Get the highest price (first element)
    const highestPrice = prices[0];
    
    // Get the second highest price (or highest if only one price exists)
    const secondHighestPrice = prices.length > 1 ? prices[1] : prices[0];
    
    // Use second highest price, ignoring the outlier
    const maxPrice = secondHighestPrice.price;
    const maxPriceProperty = secondHighestPrice;
    
    console.log('Calculated max price from properties:', {
      totalProperties: properties.length,
      validPrices: prices.length,
      highestPrice: highestPrice.price,
      highestProperty: highestPrice.token_id,
      secondHighestPrice: maxPrice,
      secondHighestProperty: maxPriceProperty.token_id,
      usingSecondHighest: prices.length > 1,
      note: prices.length > 1 ? 'Ignoring highest outlier' : 'Only one price available'
    });
    
    return maxPrice > 0 ? maxPrice : null;
  }, [properties]);
  
  // Get the actual max price from properties
  const actualMaxPrice = calculateMaxPriceFromProperties();
  
  // ALWAYS prioritize calculated max price from properties over backend value
  // Backend might return raw format prices or incorrect values, so we ignore it when properties are available
  // Use useMemo to recalculate when dependencies change
  const effectiveMaxPrice = useMemo(() => {
    const calculated = actualMaxPrice !== null ? actualMaxPrice : null;
    const backend = priceRangeStats.maxPrice || 10000;
    
    // If we have properties and calculated max, ALWAYS use it (ignore backend completely)
    // Only use backend if properties are not available
    const effective = calculated !== null ? calculated : backend;
    
    console.log('Effective max price calculation:', { 
      calculated, 
      backend, 
      effective, 
      propertiesCount: properties.length,
      usingCalculated: calculated !== null,
      note: calculated !== null ? 'Using calculated from properties (ignoring backend)' : 'Using backend (no properties available)'
    });
    
    return effective;
  }, [actualMaxPrice, priceRangeStats.maxPrice, properties.length]);

  // Fetch price range statistics on component mount only
  useEffect(() => {
    if (isInitialized.current) return;
    
    const fetchPriceStats = async () => {
      dispatch(setIsLoadingPriceStats(true));
      try {
        const stats = await getPriceRangeStats();
        console.log('Backend price stats received:', stats);
        
        // Calculate actual max price from properties - ALWAYS prioritize this
        const calculatedMaxPrice = calculateMaxPriceFromProperties();
        
        // ALWAYS use calculated max price from properties if available, completely ignore backend
        // Backend might return raw format prices or incorrect values
        const finalMaxPrice = calculatedMaxPrice !== null
          ? calculatedMaxPrice
          : (stats.maxPrice || 10000);
        
        console.log('🔄 Initial setup - Setting final max price to:', finalMaxPrice, 
          '(calculated from properties:', calculatedMaxPrice, 
          ', backend returned:', stats.maxPrice, 
          ', properties count:', properties.length,
          ', using calculated:', calculatedMaxPrice !== null ? 'YES' : 'NO (fallback to backend)');
        
        // Update stats with the actual max price from properties (this overrides backend value)
        const updatedStats = {
          ...stats,
          maxPrice: finalMaxPrice
        };
        
        dispatch(setPriceRangeStats(updatedStats));
        
        // ALWAYS update the price range to use calculated max, even if backend says 500000
        const newRange = {
          min: 0, // Set minimum to 0 as requested
          max: Math.max(1, finalMaxPrice)
        };
        
        // Force update if range is different OR if current max is 500000 (backend default)
        if (newRange.min !== range.min || newRange.max !== range.max || range.max === 500000) {
          console.log('🔄 FORCING initial range update from', range.max, 'to', newRange.max);
          dispatch(setPriceRange(newRange));
          onRangeChange(newRange);
        }
      } catch (error) {
        console.error('Error fetching price stats:', error);
      } finally {
        dispatch(setIsLoadingPriceStats(false));
      }
    };

    fetchPriceStats();
    isInitialized.current = true;
  }, [calculateMaxPriceFromProperties, range, dispatch, onRangeChange]); // Include dependencies
  
  // Update max price when properties change - ALWAYS update to use properties max
  // This runs whenever properties change, even after initialization
  useEffect(() => {
    // Wait for initialization and ensure properties are available
    if (!isInitialized.current) {
      console.log('⏳ Waiting for initialization before updating max price from properties');
      return;
    }
    
    if (properties && properties.length > 0) {
      const calculatedMaxPrice = calculateMaxPriceFromProperties();
      if (calculatedMaxPrice !== null && calculatedMaxPrice > 0) {
        const currentMaxPrice = priceRangeStats.maxPrice || 0;
        const currentRangeMax = range.max || 0;
        
        console.log('📊 Properties available - updating max price:', {
          calculated: calculatedMaxPrice,
          currentStatsMax: currentMaxPrice,
          currentRangeMax: currentRangeMax,
          propertiesCount: properties.length,
          needsUpdate: calculatedMaxPrice !== currentRangeMax || currentRangeMax === 500000
        });
        
        // ALWAYS update to use the calculated max price from properties
        // This ensures we override any backend value (like 500000) with the actual max
        if (calculatedMaxPrice !== currentMaxPrice) {
          const updatedStats = {
            ...priceRangeStats,
            maxPrice: calculatedMaxPrice
          };
          dispatch(setPriceRangeStats(updatedStats));
          console.log('✅ Updated priceRangeStats.maxPrice to', calculatedMaxPrice);
        }
        
        // ALWAYS update the range max if it's different from calculated max OR if it's 500000
        // This ensures we override any backend value (like 500000) with the actual max from properties
        if (currentRangeMax !== calculatedMaxPrice || currentRangeMax === 500000) {
          const newRange = {
            ...range,
            max: calculatedMaxPrice
          };
          console.log('🔄 FORCING range max update from', currentRangeMax, 'to', calculatedMaxPrice, 
            '(was 500000:', currentRangeMax === 500000, ', properties count:', properties.length, ')');
          dispatch(setPriceRange(newRange));
          onRangeChange(newRange);
        } else {
          console.log('✅ Range max already correct:', currentRangeMax);
        }
      } else {
        console.log('⚠️ Calculated max price is null or 0, skipping update');
      }
    } else {
      console.log('⚠️ No properties available, properties:', properties);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties, calculateMaxPriceFromProperties]);

  // Debounced range changes to avoid too many API calls
  useEffect(() => {
    // Skip if this is the initial load
    if (!isInitialized.current) return;
    
    // Skip if range hasn't actually changed
    if (lastRangeRef.current.min === range.min && lastRangeRef.current.max === range.max) {
      return;
    }

    const timer = setTimeout(() => {
      lastRangeRef.current = range;
      fetchListingsCount(range);
    }, 300);

    return () => clearTimeout(timer);
  }, [range.min, range.max]);

  // Separate function for fetching listings count
  const fetchListingsCount = useCallback(async (currentRange) => {
    if (currentRange.min !== undefined && currentRange.max !== undefined) {
      try {
        const count = await getListingsInPriceRange(currentRange.min, currentRange.max);
        dispatch(setListingsInRange(count));
      } catch (error) {
        console.error('Error fetching listings count:', error);
      }
    }
  }, [dispatch]);

  const handleRangeChange = useCallback(([min, max]) => {
    const newRange = {
      min: Math.max(0, Math.min(min, max)), // Minimum is 0
      max: Math.min(effectiveMaxPrice, Math.max(min, max))
    };
    
    // Only update if the range actually changed
    if (newRange.min !== range.min || newRange.max !== range.max) {
      dispatch(setPriceRange(newRange));
      onRangeChange(newRange);
    }
  }, [effectiveMaxPrice, range.min, range.max, dispatch, onRangeChange]);

  const handleMinChange = useCallback((newMin) => {
    handleRangeChange([newMin, range.max]);
  }, [handleRangeChange, range.max]);

  const handleMaxChange = useCallback((newMax) => {
    handleRangeChange([range.min, newMax]);
  }, [handleRangeChange, range.min]);

  // Use real data for the chart (UI towers) - keeping the structure intact
  const chartData = priceRangeStats.priceDistribution.length > 0 
    ? priceRangeStats.priceDistribution 
    : Array(30).fill(0);

  return (
    <div>
      <div className="mt-[15px] sm:mt-[20px]">
        <div className="text-[16px] sm:text-[18px] font-semibold">Price range</div>
        {!isLoadingPriceStats && (
          <div className="text-[12px] sm:text-[14px] text-gray-600 mt-1">
            {listingsInRange} listings available
          </div>
        )}
      </div>
      <div className="w-full">
        <div className="w-full mx-auto space-y-2 filter-slider-mobile">
          {/* UI Towers - keeping exactly as they are */}
          <CustomChart
            data={chartData}
            start={range.min}
            end={range.max}
            min={0} // Set minimum to 0
            max={effectiveMaxPrice}
          />
          <RangeSlider
            max={effectiveMaxPrice}
            min={0} // Set minimum to 0
            value={[range.min, range.max]}
            onInput={handleRangeChange}
            disabled={isLoadingPriceStats}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-[15px] sm:gap-[20px] my-[12px] sm:my-[16px] font-semibold">
        <PriceInput
          label="Minimum"
          value={range.min}
          onChange={handleMinChange}
          min={0} // Set minimum to 0
          max={effectiveMaxPrice}
        />
        <PriceInput
          label="Maximum"
          value={range.max}
          onChange={handleMaxChange}
          min={0} // Set minimum to 0
          max={effectiveMaxPrice}
        />
      </div>
      {isLoadingPriceStats && (
        <div className="text-[12px] text-gray-500 text-center">
          Loading price data...
        </div>
      )}
    </div>
  );
};
